import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Circle, Rocket, Save, Loader2, MessageCircle } from 'lucide-react';
import { CJMSClient, cjmsApi } from '../../api/cjmsApi';
import { feedbackBus } from '../../services/feedbackBus';

interface ActivationTasksProps {
    client: CJMSClient;
}

const ACTIVATION_TASKS = [
    { id: 'profile', label: 'Complete Business Profile', desc: 'Fill in company details, logo, and description' },
    { id: 'onboarding', label: 'Client Onboarding Call', desc: 'Walk through platform features and next steps' },
    { id: 'content', label: 'Initial Content Setup', desc: 'Upload first set of marketing assets' },
    { id: 'integrations', label: 'Connect Integrations', desc: 'Link social media, analytics, and payment tools' },
    { id: 'training', label: 'Staff Training', desc: 'Train team on platform usage and workflows' },
];

export const ActivationTasks: React.FC<ActivationTasksProps> = ({ client }) => {
    const isEligible = client.currentStage === 'Fulfillment';
    const [taskStatus, setTaskStatus] = useState<Record<string, boolean>>({});
    const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

    useEffect(() => {
        // Initialize tasks as completed
        const initialStatus: Record<string, boolean> = {};
        for (const task of ACTIVATION_TASKS) {
            initialStatus[task.id] = true;
        }
        setTaskStatus(initialStatus);
    }, []);

    if (!isEligible) return null;

    const completedCount = Object.values(taskStatus).filter(Boolean).length;
    const progress = Math.round((completedCount / ACTIVATION_TASKS.length) * 100);

    const toggleTask = (id: string) => {
        setTaskStatus(prev => {
            const newStatus = { ...prev, [id]: !prev[id] };

            // Check if this is the last task being marked complete
            if (!prev[id] && completedCount + 1 >= ACTIVATION_TASKS.length) {
                feedbackBus.emit('milestone_reached', {
                    milestoneId: 'activation_complete',
                    milestoneLabel: 'Activation Complete',
                    description: 'All activation tasks completed',
                });
            }

            return newStatus;
        });
    };

    const handleSave = async () => {
        setStatus('saving');
        try {
            // Save to milestones API
            const result = await cjmsApi.checkMilestones({
                leadId: client.id,
                email: client.email,
            });

            if (result.success) {
                feedbackBus.emit('activity_completed', {
                    activityLabel: `Activation tasks saved (${completedCount}/${ACTIVATION_TASKS.length})`,
                    clientEmail: client.email,
                    milestones: result.milestones,
                });
                setStatus('saved');
                setTimeout(() => setStatus('idle'), 2000);
            } else {
                setStatus('idle');
            }
        } catch {
            setStatus('idle');
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <div className="flex items-center gap-2">
                    <Rocket className="w-4 h-4 text-emerald-600" />
                    <h3 className="text-sm font-bold text-slate-800">Activation Tasks</h3>
                </div>
                <div className="flex items-center gap-2">
                    <div className="w-20 h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                            initial={{ width: 0 }}
                            animate={{ width: `${progress}%` }}
                            className="h-full bg-emerald-500 rounded-full"
                        />
                    </div>
                    <span className="text-[10px] font-medium text-slate-500">{completedCount}/{ACTIVATION_TASKS.length}</span>
                </div>
            </div>
            <div className="p-4 space-y-2">
                {ACTIVATION_TASKS.map(task => (
                    <div
                        key={task.id}
                        className={`flex items-start gap-3 p-3 rounded-lg border transition-all ${
                            taskStatus[task.id]
                                ? 'bg-green-50 border-green-200'
                                : 'bg-white border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/30'
                        }`}
                        onClick={() => toggleTask(task.id)}
                    >
                        {taskStatus[task.id]
                            ? <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                            : <Circle className="w-4 h-4 text-slate-300 mt-0.5 shrink-0" />}
                        <div className="flex-1 min-w-0">
                            <span className={`text-xs font-semibold ${taskStatus[task.id] ? 'text-green-700 line-through' : 'text-slate-700'}`}>
                                {task.label}
                            </span>
                            <p className="text-[10px] text-slate-500 mt-0.5">{task.desc}</p>
                        </div>
                    </div>
                ))}
                <div className="flex justify-end pt-2 border-t border-slate-100 mt-4">
                    <button
                        onClick={handleSave}
                        disabled={status === 'saving'}
                        className="px-3 py-1.5 text-[11px] font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                    >
                        {status === 'saving' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                        Save Progress
                    </button>
                </div>
            </div>

            {/* Support */}
            <div className="px-4 pb-4">
                <div className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg border border-slate-100">
                    <MessageCircle className="w-3 h-3 text-slate-400" />
                    <p className="text-[9px] text-slate-600">
                        Questions about activation? WhatsApp +27 81 714 2228 — responded within 6-12 hours.
                    </p>
                </div>
            </div>
        </motion.div>
    );
};
